import React from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/sections/Footer';
import Seo from '../components/Seo';
import useProducts from '../hooks/useProducts';
import { businessInfo } from '../mock';
import { getBreadcrumbJsonLd, SITE_URL } from '../lib/structuredData';

const num = (n) => typeof n === 'number' && !Number.isNaN(n) && n > 0;
const fmt = (n) => (num(n) ? `$${n.toFixed(2)}` : null);

// Etiqueta de precio para el catálogo /menu. Refleja la MISMA informacion que
// la home: porcion/completo en brazos, tamaños en pasteles, "desde" cuando el
// precio mostrado es un punto de partida (opciones). No inventa precios: solo
// muestra los que existen y estan disponibles.
const priceLabel = (item) => {
  // Brazos gitanos: porcion y/o completo (solo las formas disponibles).
  const sliceOk = num(item.priceSlice) && item.availableSlice !== false;
  const fullOk = num(item.priceFull) && item.availableFull !== false;
  if (sliceOk || fullOk) {
    const parts = [];
    if (sliceOk) parts.push(`Porción ${fmt(item.priceSlice)}`);
    if (fullOk) parts.push(`Completo ${fmt(item.priceFull)}`);
    return parts.join(' · ');
  }

  // Pasteles con tamaños (La Reina, Tres Leches): el precio real vive en sizes.
  if (Array.isArray(item.sizes) && item.sizes.length) {
    const sized = item.sizes.filter((s) => num(s.price));
    if (sized.length) {
      // Tres Leches: rango "desde" el tamaño mas economico.
      if (item.isTresLeches) {
        const min = Math.min(...sized.map((s) => s.price));
        return `desde ${fmt(min)}`;
      }
      // La Reina y similares: listar cada tamaño con sus porciones.
      return sized
        .map((s) => `${s.name}${s.portions ? ` (${s.portions})` : ''} ${fmt(s.price)}`)
        .join(' · ');
    }
  }

  // Galletas: precio por unidad.
  if (num(item.priceUnit)) return `Unidad ${fmt(item.priceUnit)}`;

  // Items con opciones/variantes (p. ej. Café y Té): el precio base es un "desde".
  const hasOptions = (Array.isArray(item.options) && item.options.length > 0) || item.hasOption;
  if (hasOptions && num(item.price)) return `desde ${fmt(item.price)}`;

  // Precio simple.
  return fmt(item.price);
};

// ---- JSON-LD de productos (offers/price) para rich results ----
const USD = 'USD';
const IN_STOCK = 'https://schema.org/InStock';
const BRAND = { '@type': 'Brand', name: 'Brazolovers' };

// Delivery local real: $2.00 base (hasta 4 km) en Quito/Ecuador. Refleja la
// tarifa de partida del envío a domicilio (el retiro en tienda es gratis).
const SHIPPING_DETAILS = {
  '@type': 'OfferShippingDetails',
  shippingRate: { '@type': 'MonetaryAmount', value: '2.00', currency: USD },
  shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'EC' },
  deliveryTime: {
    '@type': 'ShippingDeliveryTime',
    handlingTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
    transitTime: { '@type': 'QuantitativeValue', minValue: 0, maxValue: 1, unitCode: 'DAY' },
  },
};

const singleOffer = (price) => ({
  '@type': 'Offer',
  priceCurrency: USD,
  price: Number(price).toFixed(2),
  availability: IN_STOCK,
  shippingDetails: SHIPPING_DETAILS,
});

const aggregateOffer = (prices) => ({
  '@type': 'AggregateOffer',
  priceCurrency: USD,
  lowPrice: Math.min(...prices),
  highPrice: Math.max(...prices),
  offerCount: prices.length,
  availability: IN_STOCK,
  shippingDetails: SHIPPING_DETAILS,
});

// Construye el bloque offers reflejando la MISMA info que se ve en pantalla.
const offersFor = (it) => {
  const sliceOk = num(it.priceSlice) && it.availableSlice !== false;
  const fullOk = num(it.priceFull) && it.availableFull !== false;
  if (sliceOk && fullOk) return aggregateOffer([it.priceSlice, it.priceFull]);
  if (sliceOk) return singleOffer(it.priceSlice);
  if (fullOk) return singleOffer(it.priceFull);

  if (Array.isArray(it.sizes) && it.sizes.length) {
    const prices = it.sizes.map((s) => s.price).filter(num);
    if (prices.length > 1) return aggregateOffer(prices);
    if (prices.length === 1) return singleOffer(prices[0]);
  }

  if (num(it.priceUnit)) return singleOffer(it.priceUnit);
  if (num(it.price)) return singleOffer(it.price);
  return null;
};

const absImage = (src) => {
  if (!src) return undefined;
  if (/^https?:\/\//.test(src)) return src;
  return `${SITE_URL}${src.startsWith('/') ? '' : '/'}${src}`;
};

const productNode = (it) => {
  const offers = offersFor(it);
  if (!offers) return null;
  const node = { '@type': 'Product', name: it.name, brand: BRAND };
  if (it.description) node.description = it.description;
  const img = absImage(it.image);
  if (img) node.image = img;
  node.offers = offers;
  return node;
};

// ItemList con un Product (offers/price) por cada ítem disponible del menú.
const buildMenuJsonLd = (items) => {
  const nodes = items
    .filter((it) => it && it.available !== false)
    .map(productNode)
    .filter(Boolean);
  if (!nodes.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Menú de Brazolovers',
    itemListElement: nodes.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item,
    })),
  };
};

const MenuGroup = ({ id, title, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <section id={id} className="mb-10">
      <h2 className="text-2xl font-bold text-chocolate mb-4 border-b border-caramel/40 pb-2">
        {title}
      </h2>
      <ul className="grid sm:grid-cols-2 gap-4">
        {items
          .filter((it) => it.available !== false)
          .map((it) => {
            const price = priceLabel(it);
            return (
              <li key={it.id || it.name} className="bg-white rounded-xl shadow-sm p-4">
                <h3 className="font-semibold text-chocolate">{it.name}</h3>
                {price && (
                  <p className="text-caramel font-medium text-sm mt-0.5">{price}</p>
                )}
                {it.description && (
                  <p className="text-sm text-chocolate-light mt-1">{it.description}</p>
                )}
              </li>
            );
          })}
      </ul>
    </section>
  );
};

const Menu = () => {
  const {
    brazosGitanos, galletas, galletasEspeciales, packs, pasteles,
    panaderia, desayunos, menuSalado, bebidasFrias, bebidasCalientes,
    loadingProducts,
  } = useProducts();

  const breadcrumb = getBreadcrumbJsonLd([
    { name: 'Inicio', path: '/' },
    { name: 'Menú', path: '/menu/' },
  ]);
  const menuJsonLd = buildMenuJsonLd([
    ...brazosGitanos, ...pasteles, ...galletas, ...galletasEspeciales, ...packs,
    ...panaderia, ...desayunos, ...menuSalado, ...bebidasFrias, ...bebidasCalientes,
  ]);
  const jsonLd = [breadcrumb, menuJsonLd].filter(Boolean);

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Seo
        title="Menú de Brazolovers | Brazos gitanos, pasteles y más en Quito"
        description="Explora el menú completo de Brazolovers: más de 40 sabores de brazos gitanos artesanales, pasteles, galletas, panadería, desayunos y bebidas. Pide en línea en Quito."
        path="/menu/"
        jsonLd={jsonLd}
      />
      <SiteHeader />

      <main className="container mx-auto px-4 md:px-6 py-10 flex-1">
        <h1 className="text-3xl md:text-4xl font-bold text-chocolate mb-2">
          Menú de Brazolovers
        </h1>
        <p className="text-chocolate-light mb-8 max-w-2xl">
          Pastelería artesanal en Quito con más de 40 sabores de brazos gitanos, además de
          pasteles, galletas, panadería, desayunos y bebidas. ¿Listo para pedir?{' '}
          <Link to="/" className="text-caramel font-medium hover:underline">
            Haz tu pedido en línea
          </Link>.
        </p>

        {loadingProducts && (
          <p className="text-chocolate-light">Cargando menú…</p>
        )}

        <MenuGroup id="brazos" title="Brazos Gitanos" items={brazosGitanos} />
        <MenuGroup id="pasteles" title="Pasteles" items={pasteles} />
        <MenuGroup id="galletas" title="Galletas" items={galletas} />
        <MenuGroup id="galletas-especiales" title="Galletas Especiales" items={galletasEspeciales} />
        <MenuGroup id="packs" title="Packs" items={packs} />
        <MenuGroup id="panaderia" title="Panadería" items={panaderia} />
        <MenuGroup id="desayunos" title="Desayunos" items={desayunos} />
        <MenuGroup id="menu-salado" title="Menú Salado" items={menuSalado} />
        <MenuGroup id="bebidas-frias" title="Bebidas Frías" items={bebidasFrias} />
        <MenuGroup id="bebidas-calientes" title="Bebidas Calientes" items={bebidasCalientes} />
      </main>

      <Footer businessInfo={businessInfo} />
    </div>
  );
};

export default Menu;
